import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText } from 'lucide-react';
import { useClients } from '../hooks/useClients';
import { useMatters } from '../hooks/useMatters';
import { usePageNavigation } from '../contexts/NavigationContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import MatterList from '../components/matters/MatterList';
import SearchBar from '../components/ui/SearchBar';
import WeeklyProductivityWidget from '../components/dashboard/WeeklyProductivityWidget';
import RevenueCard from '../components/dashboard/RevenueCard';
import { MedicalReviewDashboard } from '../components/medical-records/MedicalReviewDashboard';
import { DashboardSkeleton } from '../components/ui/LoadingSkeleton';
import { FadeInSection } from '../components/ui/FadeInSection';
import type { Matter } from '../types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { clients } = useClients();
  const { matters, isLoading: mattersLoading, loadMatters } = useMatters();
  const { setPageNavigation } = usePageNavigation();
  const { features, isLoading: featuresLoading } = useFeatureFlags();

  // Feature flags with safe defaults
  const showReportCards = features?.dashboardShowReportCards || false;
  const showMattersList = features?.dashboardShowMattersList || false;
  const showMedicalUpload = features?.medicalRecordsUploadEnabled || false;

  // Set up page navigation
  React.useEffect(() => {
    setPageNavigation('Dashboard', [
      { label: 'Dashboard', href: '/' }
    ]);
  }, [setPageNavigation]);

  // Load matters on component mount
  React.useEffect(() => {
    loadMatters(); // Load all matters (no clientId)
  }, [loadMatters]);

  // Calculate active matters count
  const activeMattersCount = React.useMemo(() => {
    return matters.filter(matter => matter.status === 'active').length;
  }, [matters]);

  // Sort matters by lastActivity (most recent first) and limit to 10 for dashboard
  const recentMatters = React.useMemo(() => {
    return matters
      .sort((a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime())
      .slice(0, 10);
  }, [matters]);

  // Handle matter click - navigate to matter details
  const handleMatterClick = (matter: Matter) => {
    navigate(`/matters/${matter.id}`);
  };

  // Show loading skeleton while features are being loaded
  if (featuresLoading) {
    return (
      <div className="space-y-8">
        {/* Search Bar - always show */}
        <div className="w-full">
          <SearchBar />
        </div>
        
        {/* Smart skeleton that shows potential sections */}
        <DashboardSkeleton 
          showReportCards={true}
          showMattersList={true} 
          showMedicalUpload={true}
        />
      </div>
    );
  }


  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <div className="w-full">
        <SearchBar />
      </div>

            {/* Stats Cards - Conditionally rendered based on feature flag */}
      <FadeInSection isVisible={showReportCards} delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-400" />
              <div className="ml-4">
                <h3 className="text-xl font-semibold text-white">{clients.length}</h3>
                <p className="text-gray-400">Active Clients</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-green-400" />
              <div className="ml-4">
                <h3 className="text-xl font-semibold text-white">{activeMattersCount}</h3>
                <p className="text-gray-400">Active Matters</p>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue and Weekly Productivity Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Card - 1/3 width */}
          <div className="lg:col-span-1">
            <RevenueCard />
          </div>
          
          {/* Weekly Productivity Chart - 2/3 width */}
          <div className="lg:col-span-2">
            <WeeklyProductivityWidget />
          </div>
        </div>
      </FadeInSection>

            {/* Recent Matters Section - Conditionally rendered based on feature flag */}
      <FadeInSection isVisible={showMattersList} delay={200}>
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-6 pb-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Recent Matters</h2>
              <p className="text-sm text-gray-400">
                {activeMattersCount} active matter{activeMattersCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          {mattersLoading ? (
            <div className="p-6 text-center">
              <div className="text-gray-400">Loading matters...</div>
            </div>
          ) : recentMatters.length > 0 ? (
            <MatterList 
              matters={recentMatters}
              isLoading={mattersLoading}
              onMatterClick={handleMatterClick}
              className="border-0" // Remove border since parent container has border
            />
          ) : (
            <div className="p-6 text-center">
              <div className="text-gray-400">No matters found</div>
            </div>
          )}
        </div>
      </FadeInSection>

      {/* Medical Records Dashboard - Conditionally rendered based on feature flag */}
      {showMedicalUpload && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <MedicalReviewDashboard 
            matters={matters}
            className=""
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
